/**
 * Solicitação inicial do atendimento (spec 018, US3): a descrição como o
 * cliente a mandou — e, quando é a ficha do SGR, renderizada COMO FICHA (pares
 * rótulo → valor no vocabulário do tenant, sgrSheetEntries), nunca o HTML cru
 * do TiFlux. Texto livre vira texto (htmlToPlainText) — nada de HTML injetado.
 *
 * Editável pelo lápis do cabeçalho (pedido da PoolTrack, 03/10): o mesmo
 * editor da visão completa (features/tickets/description-editor). Salvo, o
 * detalhe do ticket é relido e a ficha acima se refaz do texto novo.
 */
import { useState } from 'react';
import {
  ORIGIN_LABELS,
  htmlToPlainText,
  parseSgrDescription,
  sgrSheetEntries,
  type TicketDetail,
} from '@erp/shared';
import { FileText } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import {
  DescriptionEditButton,
  DescriptionEditor,
  DescriptionPhoneWarning,
} from '@/features/tickets/description-editor';
import { TicketSheetCard, useSheetProfile } from '@/features/tickets/ticket-sheet-card';

export function RequestCard({ ticket }: { ticket: TicketDetail }) {
  const terms = useTerms();
  const sheetProfile = useSheetProfile();
  const [showRaw, setShowRaw] = useState(false);
  const [editing, setEditing] = useState(false);
  const sheet = parseSgrDescription(ticket.description);
  // spec 031: nos perfis com ficha, a ficha é o cartão (com a correção da
  // equipe e a origem de cada valor), e a descrição fica como texto abaixo
  const entries = !sheetProfile && sheet.recognized ? sgrSheetEntries(sheet, terms) : [];
  const text = htmlToPlainText(ticket.description);
  const who = ticket.requestorName ?? ticket.client.name;

  return (
    <section aria-label="Solicitação inicial" className="rounded-lg border bg-card p-4 shadow-sm">
      <header className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        <FileText className="size-3.5" aria-hidden />
        <span className="font-semibold text-foreground">Solicitação</span>
        <span>{who}</span>
        <span>·</span>
        <span>{ORIGIN_LABELS[ticket.createdVia]}</span>
        <span>·</span>
        <span className="tabular-nums">{formatDateTime(ticket.createdAt)}</span>
        {!editing ? (
          <DescriptionEditButton ticket={ticket} onEdit={() => setEditing(true)} className="ml-auto" />
        ) : null}
      </header>
      {editing ? (
        <DescriptionEditor ticket={ticket} onDone={() => setEditing(false)} />
      ) : (
        <>
          <DescriptionPhoneWarning ticket={ticket} className="mb-2" />
          {sheetProfile ? (
            <>
              <TicketSheetCard ticket={ticket} variant="inline" />
              {sheet.recognized && text ? (
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  className="mt-2 text-xs font-medium text-primary hover:underline"
                >
                  {showRaw ? 'Esconder a descrição original' : 'Ver a descrição original'}
                </button>
              ) : null}
              {sheet.recognized && showRaw ? (
                <p className="mt-1 text-xs whitespace-pre-wrap text-muted-foreground">{text}</p>
              ) : null}
              {!sheet.recognized && text ? (
                <p className="mt-2 border-t pt-2 text-sm break-words whitespace-pre-wrap text-foreground">{text}</p>
              ) : null}
            </>
          ) : entries.length > 0 ? (
            <>
              <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1 text-sm">
                {entries.map((e) => (
                  <div key={e.label} className="contents">
                    <dt className="text-muted-foreground">{e.label}</dt>
                    <dd className="min-w-0 break-words text-foreground">{e.value}</dd>
                  </div>
                ))}
              </dl>
              {text ? (
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  className="mt-2 text-xs font-medium text-primary hover:underline"
                >
                  {showRaw ? 'Esconder o texto original' : 'Ver o texto original'}
                </button>
              ) : null}
              {showRaw ? <p className="mt-1 text-xs whitespace-pre-wrap text-muted-foreground">{text}</p> : null}
            </>
          ) : text ? (
            <p className="text-sm break-words whitespace-pre-wrap text-foreground">{text}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Sem descrição.</p>
          )}
        </>
      )}
    </section>
  );
}
